/**
 * The Fabrique — Wishlist
 * ----------------------------------------------------------------------------
 * localStorage-based wishlist. No backend required.
 *
 * Public API:
 *   window.WishlistStore.{get, has, add, remove, toggle, count, clear, addMany}
 *
 * Custom elements:
 *   <wishlist-button data-handle="...">    — heart toggle
 *   <wishlist-count>                       — count badge (auto-hides at 0)
 *   <wishlist-page>                        — wishlist page renderer
 * ----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'fabrique_wishlist';
  var memoryFallback = null;

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return memoryFallback || [];
    }
  }

  function write(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (e) {
      memoryFallback = arr;
    }
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { items: read(), count: read().length }
    }));
  }

  var WishlistStore = {
    get: read,
    count: function () { return read().length; },
    has: function (handle) { return read().indexOf(handle) !== -1; },
    add: function (handle) {
      if (!handle || this.has(handle)) return;
      var items = read();
      items.unshift(handle);
      write(items);
      document.dispatchEvent(new CustomEvent('wishlist:item-added', { detail: { handle: handle } }));
    },
    addMany: function (handles) {
      if (!Array.isArray(handles) || !handles.length) return 0;
      var existing = read();
      var added = 0;
      handles.forEach(function (h) {
        if (h && existing.indexOf(h) === -1) {
          existing.unshift(h);
          added++;
        }
      });
      if (added > 0) write(existing);
      return added;
    },
    remove: function (handle) {
      if (!handle) return;
      write(read().filter(function (h) { return h !== handle; }));
      document.dispatchEvent(new CustomEvent('wishlist:item-removed', { detail: { handle: handle } }));
    },
    toggle: function (handle) {
      if (this.has(handle)) { this.remove(handle); return false; }
      this.add(handle);
      return true;
    },
    clear: function () { write([]); }
  };

  window.WishlistStore = WishlistStore;

  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { items: read(), count: read().length }
      }));
    }
  });

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ========================================================================
     Cart refresh helper — hits every modern theme's cart-update mechanism.
     Called after any AJAX add-to-cart from the wishlist UI.
     ======================================================================== */
  async function refreshCart(addedItem) {
    var cart = null;
    try {
      var res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (res.ok) cart = await res.json();
    } catch (e) { /* ignore */ }

    // Update any cart count badges in the page
    if (cart) {
      var count = cart.item_count;
      document.querySelectorAll('cart-count, [data-cart-count], .cart-count').forEach(function (el) {
        // Skip wishlist-count
        if (el.tagName && el.tagName.toLowerCase() === 'wishlist-count') return;
        if (el.classList && el.classList.contains('wishlist-count')) return;

        // Update text content
        if (count > 99) {
          el.innerHTML = '<span class="text-sm-extra">99+</span>';
        } else {
          el.textContent = count;
        }
        if (count === 0) el.setAttribute('hidden', '');
        else el.removeAttribute('hidden');
      });
    }

    // Dispatch every event modern themes listen for
    var events = [
      'cart:updated',
      'cart:item-added',
      'cart:refresh',
      'cart:change',
      'cart-updated',
      'cart-update',
      'on:cart:after-add'
    ];
    events.forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail: { cart: cart, item: addedItem }
      }));
    });

    // PUB/SUB pattern used by Dawn-based themes
    if (window.PUB_SUB_EVENTS && window.publish) {
      try {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'wishlist',
          productVariantId: addedItem ? (addedItem.variant_id || addedItem.id) : null,
          cartData: cart
        });
      } catch (e) { /* ignore */ }
    }

    // Tell theme's <cart-drawer> / <cart-notification> to refresh from the source
    // by re-fetching their section and replacing innerHTML if exposed
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && cart) {
      try { drawer.renderContents({ sections: cart.sections, sections_url: cart.sections_url }); }
      catch (e) { /* ignore */ }
    }
    var notification = document.querySelector('cart-notification');
    if (notification && typeof notification.renderContents === 'function' && cart) {
      try { notification.renderContents({ sections: cart.sections, sections_url: cart.sections_url }); }
      catch (e) { /* ignore */ }
    }

    // Open the cart drawer if it exists and the theme typically auto-opens it
    if (drawer && typeof drawer.open === 'function') {
      try { drawer.open(); } catch (e) { /* ignore */ }
    }
  }

  /* ========================================================================
     <wishlist-button>
     ======================================================================== */
  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', class extends HTMLElement {
      connectedCallback() {
        this.handle = this.getAttribute('data-handle');
        this.btn = this.querySelector('button');
        if (!this.btn || !this.handle) return;

        this._onClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          WishlistStore.toggle(this.handle);
        };
        this._onUpdate = () => this.sync();

        this.btn.addEventListener('click', this._onClick);
        document.addEventListener('wishlist:updated', this._onUpdate);
        this.sync();
      }

      disconnectedCallback() {
        if (this.btn && this._onClick) this.btn.removeEventListener('click', this._onClick);
        if (this._onUpdate) document.removeEventListener('wishlist:updated', this._onUpdate);
      }

      sync() {
        var active = WishlistStore.has(this.handle);
        this.classList.toggle('is-active', active);
        this.btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        this.btn.setAttribute(
          'aria-label',
          active
            ? (this.getAttribute('data-label-remove') || 'Remove from wishlist')
            : (this.getAttribute('data-label-add') || 'Add to wishlist')
        );
      }
    });
  }

  /* ========================================================================
     <wishlist-count>
     ======================================================================== */
  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', class extends HTMLElement {
      connectedCallback() {
        this._onUpdate = () => this.update();
        document.addEventListener('wishlist:updated', this._onUpdate);
        this.update();
      }
      disconnectedCallback() {
        if (this._onUpdate) document.removeEventListener('wishlist:updated', this._onUpdate);
      }
      update() {
        var c = WishlistStore.count();
        this.textContent = c;
        if (c === 0) this.setAttribute('hidden', '');
        else this.removeAttribute('hidden');
      }
    });
  }

  /* ========================================================================
     <wishlist-page>
     ======================================================================== */
  if (!customElements.get('wishlist-page')) {
    customElements.define('wishlist-page', class extends HTMLElement {
      connectedCallback() {
        this.itemsEl = this.querySelector('[data-wl-items]');
        this.emptyEl = this.querySelector('[data-wl-empty]');
        this.countEl = this.querySelector('[data-wl-count]');
        this.loadingEl = this.querySelector('[data-wl-loading]');
        this.shareWrap = this.querySelector('[data-wl-share]');
        this.sharedBanner = this.querySelector('[data-wl-shared-banner]');
        this.saveAllBtn = this.querySelector('[data-wl-save-all]');
        this.ownTitle = this.querySelector('[data-wl-own-title]');
        this.sharedTitle = this.querySelector('[data-wl-shared-title]');
        this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';

        var params = new URLSearchParams(window.location.search);
        var sharedParam = params.get('items');
        this.mode = 'own';
        this.sharedHandles = [];
        if (sharedParam) {
          this.sharedHandles = sharedParam
            .split(',')
            .map(function (s) { return s.trim(); })
            .filter(Boolean)
            .slice(0, 50);
          if (this.sharedHandles.length) this.mode = 'shared';
        }

        this.applyMode();

        this._onUpdate = () => { if (this.mode === 'own') this.render(); };
        document.addEventListener('wishlist:updated', this._onUpdate);

        this.bindShareButtons();
        this.render();
      }

      disconnectedCallback() {
        if (this._onUpdate) document.removeEventListener('wishlist:updated', this._onUpdate);
      }

      applyMode() {
        if (this.mode === 'shared') {
          this.classList.add('is-shared-mode');
          if (this.sharedBanner) this.sharedBanner.hidden = false;
          if (this.shareWrap) this.shareWrap.hidden = true;
          if (this.ownTitle) this.ownTitle.hidden = true;
          if (this.sharedTitle) this.sharedTitle.hidden = false;
        } else {
          this.classList.remove('is-shared-mode');
          if (this.sharedBanner) this.sharedBanner.hidden = true;
          if (this.shareWrap) this.shareWrap.hidden = false;
          if (this.ownTitle) this.ownTitle.hidden = false;
          if (this.sharedTitle) this.sharedTitle.hidden = true;
        }
      }

      bindShareButtons() {
        var self = this;

        if (this.saveAllBtn) {
          this.saveAllBtn.addEventListener('click', function (e) {
            e.preventDefault();
            var added = WishlistStore.addMany(self.sharedHandles);
            self.saveAllBtn.disabled = true;
            var orig = self.saveAllBtn.textContent;
            self.saveAllBtn.textContent = added > 0
              ? 'Saved ' + added + ' item' + (added === 1 ? '' : 's') + ' ✓'
              : 'Already in your wishlist';
            setTimeout(function () {
              self.saveAllBtn.textContent = orig;
              self.saveAllBtn.disabled = false;
            }, 2000);
          });
        }

        this.querySelectorAll('[data-wl-share-action]').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            var action = btn.getAttribute('data-wl-share-action');
            var url = self.buildShareUrl();
            if (!url) return;

            switch (action) {
              case 'copy':
                self.copyToClipboard(url, btn);
                break;
              case 'native':
                self.nativeShare(url);
                break;
              case 'whatsapp':
                window.open('https://wa.me/?text=' + encodeURIComponent('Check out my wishlist: ' + url), '_blank', 'noopener');
                break;
              case 'email':
                window.location.href = 'mailto:?subject=' + encodeURIComponent('My Wishlist') + '&body=' + encodeURIComponent('Check out my wishlist: ' + url);
                break;
            }
          });
        });
      }

      buildShareUrl() {
        var handles = WishlistStore.get();
        if (!handles.length) return null;
        var base = window.location.origin + window.location.pathname;
        return base + '?items=' + handles.map(encodeURIComponent).join(',');
      }

      async copyToClipboard(text, btn) {
        var labelEl = btn.querySelector('[data-wl-share-label]') || btn;
        var orig = labelEl.textContent;
        try {
          await navigator.clipboard.writeText(text);
          labelEl.textContent = 'Copied!';
        } catch (e) {
          var ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); labelEl.textContent = 'Copied!'; }
          catch (err) { labelEl.textContent = 'Press Ctrl+C'; }
          document.body.removeChild(ta);
        }
        setTimeout(function () { labelEl.textContent = orig; }, 1600);
      }

      async nativeShare(url) {
        if (!navigator.share) {
          var fakeBtn = document.createElement('div');
          fakeBtn.textContent = 'Share';
          await this.copyToClipboard(url, fakeBtn);
          return;
        }
        try {
          await navigator.share({
            title: 'My Wishlist',
            text: 'Check out my wishlist',
            url: url
          });
        } catch (e) { /* user cancelled */ }
      }

      async render() {
        var handles = this.mode === 'shared' ? this.sharedHandles : WishlistStore.get();
        if (this.countEl) this.countEl.textContent = handles.length;

        if (!handles.length) {
          if (this.emptyEl) this.emptyEl.hidden = false;
          if (this.itemsEl) this.itemsEl.innerHTML = '';
          if (this.loadingEl) this.loadingEl.hidden = true;
          return;
        }

        if (this.emptyEl) this.emptyEl.hidden = true;
        if (this.loadingEl) this.loadingEl.hidden = false;

        var products = await Promise.all(handles.map(function (h) {
          return fetch('/products/' + encodeURIComponent(h) + '.js')
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        }));

        var valid = products.filter(Boolean);

        if (this.loadingEl) this.loadingEl.hidden = true;
        this.itemsEl.innerHTML = valid.map((p) => this.cardHTML(p)).join('');
        this.bindCards();

        if (this.mode === 'own' && valid.length !== handles.length) {
          var validSet = {};
          valid.forEach(function (p) { validSet[p.handle] = true; });
          handles.forEach(function (h) { if (!validSet[h]) WishlistStore.remove(h); });
        }
      }

      cardHTML(p) {
        var variant = (p.variants || []).find(function (v) { return v.available; }) || (p.variants || [])[0];
        var available = variant && variant.available;
        var hasMultipleVariants = (p.variants || []).length > 1;
        var onSale = p.compare_at_price && p.compare_at_price > p.price;
        var img = p.featured_image || '';
        var imgUrl = img ? img.replace(/^https?:/, '') : '';
        var isShared = this.mode === 'shared';

        var atcLabel, atcAttr;
        if (!available) {
          atcLabel = 'Sold Out';
          atcAttr = ' disabled';
        } else if (hasMultipleVariants) {
          atcLabel = 'Choose Options';
          atcAttr = ' data-wl-link="/products/' + escapeHtml(p.handle) + '"';
        } else {
          atcLabel = 'Add to Cart';
          atcAttr = ' data-wl-atc="' + variant.id + '"';
        }

        var removeBtn = isShared ? '' :
          '<button type="button" class="wl-item__remove" data-wl-remove="' + escapeHtml(p.handle) + '" aria-label="Remove ' + escapeHtml(p.title) + ' from wishlist">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              '<line x1="18" y1="6" x2="6" y2="18"></line>' +
              '<line x1="6" y1="6" x2="18" y2="18"></line>' +
            '</svg>' +
          '</button>';

        return '' +
          '<article class="wl-item" data-handle="' + escapeHtml(p.handle) + '">' +
            '<div class="wl-item__media-wrap">' +
              removeBtn +
              '<a href="/products/' + escapeHtml(p.handle) + '" class="wl-item__media">' +
                (imgUrl
                  ? '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="400" height="400">'
                  : '<span class="wl-item__placeholder"></span>'
                ) +
                (onSale ? '<span class="wl-item__badge">Sale</span>' : '') +
              '</a>' +
            '</div>' +
            '<div class="wl-item__info">' +
              '<a href="/products/' + escapeHtml(p.handle) + '" class="wl-item__title">' + escapeHtml(p.title) + '</a>' +
              '<div class="wl-item__prices">' +
                '<span class="wl-item__price">' + this.money(p.price) + '</span>' +
                (onSale ? '<span class="wl-item__compare">' + this.money(p.compare_at_price) + '</span>' : '') +
              '</div>' +
              '<button type="button" class="wl-item__atc"' + atcAttr + '>' + atcLabel + '</button>' +
            '</div>' +
          '</article>';
      }

      bindCards() {
        var self = this;

        this.itemsEl.querySelectorAll('[data-wl-remove]').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            var article = btn.closest('.wl-item');
            if (article) article.classList.add('is-removing');
            setTimeout(function () {
              WishlistStore.remove(btn.getAttribute('data-wl-remove'));
            }, 180);
          });
        });

        this.itemsEl.querySelectorAll('[data-wl-link]').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            window.location.href = btn.getAttribute('data-wl-link');
          });
        });

        this.itemsEl.querySelectorAll('[data-wl-atc]').forEach(function (btn) {
          btn.addEventListener('click', async function (e) {
            e.preventDefault();
            var id = btn.getAttribute('data-wl-atc');
            if (!id || btn.disabled) return;
            var original = btn.textContent;
            btn.disabled = true;
            btn.textContent = 'Adding...';
            try {
              // Try to fetch with sections so the cart drawer can rerender
              var sections = '';
              var drawer = document.querySelector('cart-drawer');
              var notification = document.querySelector('cart-notification');
              var sectionList = [];
              if (drawer && drawer.dataset.id) sectionList.push(drawer.dataset.id);
              if (notification && notification.dataset.id) sectionList.push(notification.dataset.id);
              // Common fallback section IDs
              if (!sectionList.length) sectionList = ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];

              var body = {
                items: [{ id: parseInt(id, 10), quantity: 1 }],
                sections: sectionList.join(','),
                sections_url: window.location.pathname
              };

              var res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(body)
              });
              if (!res.ok) throw new Error('Add failed');
              var data = await res.json();
              var addedItem = data.items ? data.items[0] : data;

              btn.textContent = 'Added ✓';
              btn.classList.add('is-added');

              // Refresh cart UI everywhere
              await refreshCart(addedItem);

              setTimeout(function () {
                btn.textContent = original;
                btn.classList.remove('is-added');
                btn.disabled = false;
              }, 1400);
            } catch (err) {
              btn.textContent = original;
              btn.disabled = false;
            }
          });
        });
      }

      money(cents) {
        var amount = (cents / 100).toFixed(2);
        var amountNoDec = Math.round(cents / 100);
        var amountComma = amount.replace('.', ',');
        return this.moneyFormat
          .replace(/\{\{\s*amount\s*\}\}/g, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDec)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDec)
          .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, amount);
      }
    });
  }
})();
